import { Button, Card, Field, Logo, Muted, Screen, Stack, space } from '@biopeak/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase.ts';

/**
 * Página que o link de e-mail abre — em `/reset-password`.
 *
 * O cliente Supabase deste app tem `detectSessionInUrl: false` (decisão
 * deliberada, ver `supabase.ts`), então o código de recuperação que chega na
 * URL (`?code=...`, fluxo PKCE) não vira sessão sozinho: é preciso trocar
 * manualmente com `exchangeCodeForSession`. Essa troca cria uma sessão
 * temporária que só serve para uma coisa — `updateUser({ password })` — e é
 * exatamente o que a política de RLS/Auth do Supabase espera desse fluxo.
 *
 * Serve TANTO o médico quanto o paciente: os dois autenticam no mesmo
 * projeto Supabase, e o app do paciente (nativo, sem rota web própria) manda
 * o link de recuperação para esta mesma origem — ver `SignIn.tsx` do
 * paciente.
 */
export function ResetPassword() {
  const [status, setStatus] = useState<'trocando' | 'pronto' | 'invalido' | 'salvo'>('trocando');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // O Supabase pode enviar o link em dois formatos dependendo da configuração
    // do projeto:
    //   - PKCE:     ?code=...    (query string)
    //   - Implícito: #access_token=...&refresh_token=...  (hash fragment)
    // Tratamos os dois aqui para não depender da configuração do painel.

    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    const code = params.get('code');
    const accessToken = hash.get('access_token');
    const refreshToken = hash.get('refresh_token');
    const type = hash.get('type');

    if (code) {
      // Fluxo PKCE — troca o code por sessão
      supabase.auth.exchangeCodeForSession(code).then(({ error: exchangeError }) => {
        window.history.replaceState({}, '', window.location.pathname);
        setStatus(exchangeError ? 'invalido' : 'pronto');
      });
      return;
    }

    if (accessToken && refreshToken && type === 'recovery') {
      // Fluxo implícito — seta a sessão diretamente com os tokens do hash
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: sessionError }) => {
          window.history.replaceState({}, '', window.location.pathname);
          setStatus(sessionError ? 'invalido' : 'pronto');
        });
      return;
    }

    setStatus('invalido');
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);

    if (updateError) {
      setError(messageFor(updateError));
      return;
    }

    // Sai da sessão temporária de recuperação: o próximo passo do usuário é
    // logar de novo, com a senha nova, pelo caminho normal.
    await supabase.auth.signOut();
    setStatus('salvo');
  }

  if (status === 'trocando') {
    return (
      <Screen style={{ justifyContent: 'center', minHeight: '100vh' }}>
        <Muted>Verificando o link…</Muted>
      </Screen>
    );
  }

  if (status === 'invalido') {
    return (
      <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
        <Stack gap={space.sm}>
          <Logo variant="lockup" size={40} />
        </Stack>
        <Card>
          <Stack gap={space.md}>
            <h1 style={{ fontSize: '18px', margin: 0 }}>Link inválido ou expirado</h1>
            <Muted>
              Peça um novo link de redefinição de senha na tela de login.
            </Muted>
          </Stack>
        </Card>
      </Screen>
    );
  }

  if (status === 'salvo') {
    return (
      <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
        <Stack gap={space.sm}>
          <Logo variant="lockup" size={40} />
        </Stack>
        <Card>
          <Stack gap={space.md}>
            <h1 style={{ fontSize: '18px', margin: 0 }}>Senha atualizada</h1>
            <Muted>Já pode entrar com a senha nova.</Muted>
          </Stack>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
      <Stack gap={space.sm}>
        <Logo variant="lockup" size={40} />
        <Muted>Defina sua nova senha</Muted>
      </Stack>

      <Card>
        <form onSubmit={handleSubmit}>
          <Stack gap={space.md}>
            <Field
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="Mínimo de 8 caracteres."
            />

            {error && (
              <p role="alert" style={{ color: '#C7332B', fontSize: '15px' }}>
                {error}
              </p>
            )}

            <Button type="submit" fullWidth disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar nova senha'}
            </Button>
          </Stack>
        </form>
      </Card>
    </Screen>
  );
}

function messageFor(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/password should be at least/i.test(raw)) {
    return 'A senha precisa ter pelo menos 8 caracteres.';
  }
  if (/same.*password|different from the old/i.test(raw)) {
    return 'A nova senha precisa ser diferente da atual.';
  }
  return 'Não foi possível salvar a senha agora. Tente novamente em instantes.';
}
